/**
 * RAP Kickback Gifts — Creator panel + Configure modal
 * ----------------------------------------------------
 * Embedded into the Creator Portfolio Detail page.
 *
 * Endpoints:
 *   GET  /api/creator/portfolios/{id}/rap/status
 *   PUT  /api/creator/portfolios/{id}/rap
 *   POST /api/creator/portfolios/{id}/rap/release
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Settings, X, AlertCircle, Send } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};
const inputStyle = {
  width: '100%', padding: '10px 12px', background: theme.colors.bg.page,
  border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm,
  color: theme.colors.text.primary, fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
const labelStyle = { display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: theme.colors.text.secondary };

const STATUS_COLOR = {
  DRAFT: theme.colors.text.muted,
  LIVE: theme.colors.status.info,
  THRESHOLD_MET: theme.colors.status.success,
  RELEASED: theme.colors.accent.teal,
  EXPIRED: theme.colors.status.error,
};

export default function RapPanel({ portfolioId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/creator/portfolios/${portfolioId}/rap/status`, auth);
      setStatus(res.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load RAP status');
    } finally {
      setLoading(false);
    }
  }, [portfolioId, auth]);

  useEffect(() => { load(); }, [load]);

  const release = async () => {
    setReleasing(true);
    try {
      const res = await axios.post(`${API}/creator/portfolios/${portfolioId}/rap/release`, {}, auth);
      toast.success(`Released ${res.data?.gifts_count} gifts ($${res.data?.escrow_distributed_usd})`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Release failed');
    } finally {
      setReleasing(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="rap-panel-loading" style={{ ...cardStyle, marginTop: 16, color: theme.colors.text.muted }}>
        Loading RAP…
      </div>
    );
  }
  if (!status) return null;

  const balance = Number(status.escrow_balance_usd || 0);
  const threshold = Number(status.threshold_amount_usd || 0);
  const pct = threshold > 0 ? Math.min(100, Math.round((balance / threshold) * 100)) : 0;
  const statusColor = STATUS_COLOR[status.rap_status] || theme.colors.text.muted;

  let blockReason = null;
  if (!status.rap_enabled) blockReason = 'RAP not enabled';
  else if (status.rap_status === 'RELEASED') blockReason = 'Already released';
  else if (status.rap_status === 'EXPIRED') blockReason = 'Window expired';
  else if (status.holders_count === 0) blockReason = 'No holders';
  else if (status.eligible_holders_count === 0) blockReason = 'All holders opted out';
  else if (balance < threshold) blockReason = 'Threshold not met';

  return (
    <div data-testid="rap-panel" style={{ ...cardStyle, marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Gift size={20} color={theme.colors.primary} />
            <div style={{ fontWeight: 800, fontSize: 17, color: theme.colors.text.primary }}>
              RAP Kickback Gifts
            </div>
            <span data-testid="rap-status-pill" style={{
              padding: '3px 10px', borderRadius: 999,
              background: `${statusColor}1A`, color: statusColor,
              fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
            }}>{status.rap_status}</span>
          </div>
          <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 6 }}>
            Holders: {status.holders_count} · Opted-out: {status.opted_out_count} ·
            Eligible: {status.eligible_holders_count} · Payout: {status.payout_currency}
          </div>
        </div>
        <button
          data-testid="rap-configure-btn"
          onClick={() => setConfigOpen(true)}
          style={{
            padding: '8px 14px', borderRadius: theme.radius.sm, cursor: 'pointer',
            border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
            color: theme.colors.text.primary, fontWeight: 700, fontSize: 13,
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          <Settings size={14} /> Configure RAP
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 14 }}>
        <KV label="Escrow Balance" value={`$${balance.toFixed(2)}`} testid="rap-balance" />
        <KV label="Threshold" value={threshold ? `$${threshold.toFixed(2)}` : '—'} testid="rap-threshold" />
        <KV label="Payout Currency" value={status.payout_currency} testid="rap-currency" />
        <KV label="Window End" value={status.window_end_at ? new Date(status.window_end_at).toLocaleDateString() : '—'} testid="rap-window" />
      </div>

      <div data-testid="rap-progress" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: theme.colors.text.secondary, marginBottom: 4 }}>
          <span>Progress</span>
          <span style={{ fontWeight: 700, color: theme.colors.text.primary }}>{pct}%</span>
        </div>
        <div style={{ height: 10, background: theme.colors.bg.tertiary, borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: statusColor, transition: 'width 200ms ease' }} />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          data-testid="rap-release-btn"
          disabled={!status.eligible_to_release || releasing}
          onClick={release}
          style={{
            padding: '10px 18px', borderRadius: theme.radius.cta, border: 'none',
            background: status.eligible_to_release ? theme.colors.primary : theme.colors.bg.tertiary,
            color: status.eligible_to_release ? '#fff' : theme.colors.text.muted,
            cursor: status.eligible_to_release ? 'pointer' : 'not-allowed',
            fontWeight: 800, fontSize: 14,
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}
        >
          <Send size={14} /> {releasing ? 'Releasing…' : 'Release Kickback Gifts'}
        </button>
        {blockReason && (
          <span data-testid="rap-block-reason" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12, color: theme.colors.text.muted, fontWeight: 600,
          }}>
            <AlertCircle size={13} /> {blockReason}
          </span>
        )}
      </div>

      {configOpen && (
        <RapConfigModal
          portfolioId={portfolioId}
          initial={status}
          onClose={() => setConfigOpen(false)}
          onSaved={async () => { setConfigOpen(false); await load(); }}
        />
      )}
    </div>
  );
}

function KV({ label, value, testid }) {
  return (
    <div style={{ padding: 12, background: theme.colors.bg.secondary, borderRadius: theme.radius.sm }}>
      <div style={{ fontSize: 11, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div data-testid={testid} style={{ fontWeight: 700, color: theme.colors.text.primary, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function RapConfigModal({ portfolioId, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    rap_enabled: !!initial?.rap_enabled,
    rap_threshold_amount_usd: initial?.threshold_amount_usd || '',
    rap_window_end_at: initial?.window_end_at || '',
    rap_fail_mode: 'RETURN_ESCROW_TO_HOLDERS',
    rap_payout_currency: initial?.payout_currency || 'USD',
  });
  const [saving, setSaving] = useState(false);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const save = async () => {
    if (form.rap_enabled && (!form.rap_threshold_amount_usd || Number(form.rap_threshold_amount_usd) <= 0)) {
      toast.error('Threshold must be > 0 when RAP is enabled');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        rap_enabled: !!form.rap_enabled,
        rap_threshold_amount_usd: form.rap_enabled ? Number(form.rap_threshold_amount_usd) : null,
        rap_window_end_at: form.rap_window_end_at || null,
        rap_fail_mode: form.rap_fail_mode,
        rap_payout_currency: form.rap_payout_currency,
      };
      await axios.put(`${API}/creator/portfolios/${portfolioId}/rap`, payload, auth);
      toast.success('RAP config saved');
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="rap-config-modal" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 200,
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 'min(560px, 100%)', background: theme.colors.bg.card,
        borderRadius: theme.radius.card, padding: 24, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Configure RAP</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
          <input
            data-testid="rap-config-enabled"
            type="checkbox"
            checked={form.rap_enabled}
            onChange={(e) => setForm({ ...form, rap_enabled: e.target.checked })}
          />
          <span style={{ fontWeight: 700, color: theme.colors.text.primary }}>Enable RAP on this portfolio</span>
        </label>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Threshold Amount (USD) *</label>
          <input
            data-testid="rap-config-threshold"
            type="number" min="0" step="0.01"
            style={inputStyle}
            value={form.rap_threshold_amount_usd}
            onChange={(e) => setForm({ ...form, rap_threshold_amount_usd: e.target.value })}
            disabled={!form.rap_enabled}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Window End (ISO datetime)</label>
          <input
            data-testid="rap-config-window"
            style={inputStyle}
            placeholder="2099-12-31T00:00:00Z"
            value={form.rap_window_end_at || ''}
            onChange={(e) => setForm({ ...form, rap_window_end_at: e.target.value })}
            disabled={!form.rap_enabled}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Fail Mode</label>
          <select
            data-testid="rap-config-failmode"
            style={inputStyle}
            value={form.rap_fail_mode}
            onChange={(e) => setForm({ ...form, rap_fail_mode: e.target.value })}
            disabled={!form.rap_enabled}
          >
            <option value="RETURN_ESCROW_TO_HOLDERS">RETURN_ESCROW_TO_HOLDERS</option>
            <option value="HOLD_UNTIL_CREATOR_ACTION">HOLD_UNTIL_CREATOR_ACTION</option>
          </select>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Payout Currency</label>
          <select
            data-testid="rap-config-currency"
            style={inputStyle}
            value={form.rap_payout_currency}
            onChange={(e) => setForm({ ...form, rap_payout_currency: e.target.value })}
            disabled={!form.rap_enabled}
          >
            {['USD', 'USDC', 'ETH', 'SPLIT'].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 16px', borderRadius: theme.radius.sm, cursor: 'pointer',
              border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
              color: theme.colors.text.primary, fontWeight: 700, fontSize: 13,
            }}
          >Cancel</button>
          <button
            data-testid="rap-config-save"
            disabled={saving}
            onClick={save}
            style={{
              padding: '10px 16px', borderRadius: theme.radius.sm, cursor: 'pointer',
              border: 'none', background: theme.colors.primary, color: '#fff',
              fontWeight: 700, fontSize: 13,
            }}
          >{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
